import RelativePosition from 'Component/Render/RelativePosition';
import Game from 'Game';
import { TNTModifierScreenWarningLayout } from 'Layouts/Level/TNTModifierScreenWarningLayout';
import { createSelector, OutputSelector } from 'reselect';
import CardStateHelper from 'State/Card/CardStateHelper';
import { IGameState } from 'State/Game/IGameState';
import { isLevelCompletedSuccessfully } from 'State/Level/LevelStateHelper';
import { PopupID } from 'State/UI/PopupID';
import { StartAnimationClip } from 'System/Animation/AnimationHelper';
import { AlphaPulseLoop } from 'System/Animation/Clip';
import { MainAnim } from 'System/Animation/Clip/MainAnim';
import { StopSound } from 'System/Audio/AudioHelper';
import PIXIRenderer from 'System/Render/PIXIRenderer';
import RenderSystem, { RenderSystemEvent } from 'System/Render/RenderSystem';
import BaseUIHandler from 'System/UI/BaseUIHandler';
import BaseUISystem from 'System/UI/BaseUISystem';
import { getLayout, GLayout } from 'Utility/layout/LayoutHelper';

const TNT_MODIFIER_FULL_OVERLAY_KEY = 'TNTModifierFullOverlay';

export class TNTModifierWarningHUD extends BaseUIHandler {
    private layout: GLayout<TNTModifierScreenWarningLayout>;
    private timerSelector: OutputSelector<[(state: IGameState) => void], void, (timer: number, res1: boolean) => void>;

    constructor(system: BaseUISystem) {
        super(PopupID.TNT_MODIFIER_WARNING, system, false);
        this.alwaysVisible = true;
    }

    public preload(): Promise<void> {
        this.createOverlayTexture();
        return super.preload();
    }

    protected onCreate(): void {
        super.onCreate();

        this.layout = getLayout(TNTModifierScreenWarningLayout);
        this.layout.root.addComponent(RelativePosition, { x: 0.5, y: 0.5 });
        this.element.addChild(this.layout.root);

        this.timerSelector = createSelector(
            CardStateHelper.getLowestActiveTNTModifierTimer,
            (state: IGameState) => isLevelCompletedSuccessfully(state.levelState),
            this.handleTimerChange
        );

        RenderSystem.on(RenderSystemEvent.RESIZE, this.handleResize);
        this.cleanupManager.add(() => {
            RenderSystem.off(RenderSystemEvent.RESIZE, this.handleResize);
        });

        this.handleResize();
    }

    public handleStateChanged(state: IGameState) {
        super.handleStateChanged(state);

        this.timerSelector(state);
    }

    private readonly handleTimerChange = (timer: number, isLevelComplete: boolean): void => {
        // Level is complete
        if (isLevelComplete) {
            hide(this.layout.overlay);
            hide(this.layout.warning);
            StopSound('tnt_8_moves_Left');
            return;
        }
        // Timer is complete
        if (timer === 0) {
            hide(this.layout.warning, 3000);
            // last turn
        } else if (timer === 1) {
            StartAnimationClip(
                new AlphaPulseLoop(this.layout.overlay, {
                    duration: 1000,
                    alpha: [0.05, 0.15],
                    delay: 0,
                    fadeToStartAlphaDuration: 250,
                })
            );
        } else if (timer <= 3) {
            hide(this.layout.overlay);
            StartAnimationClip(
                new AlphaPulseLoop(this.layout.warning, {
                    duration: 750,
                    alpha: [0.7, 1],
                    delay: 0,
                    fadeToStartAlphaDuration: 250,
                })
            );
        } else {
            hide(this.layout.overlay);
            hide(this.layout.warning);
        }

        return;

        function hide(go: GameObject, delay?: number): void {
            StartAnimationClip(
                new MainAnim(go, {
                    alpha: 0,
                    duration: 150,
                    delay: delay,
                })
            );
        }
    };

    private readonly handleResize = (): void => {
        const { width, height } = PIXIRenderer.renderer;
        setSize(this.layout.warning);
        setSize(this.layout.overlay);
        return;
        function setSize(go: GameObject) {
            go.container.width = width;
            go.container.height = height;
        }
    };

    private createOverlayTexture(): void {
        const overlayGraphic = new PIXI.Graphics();
        overlayGraphic.beginFill(0xff0000);
        overlayGraphic.lineStyle(0);
        overlayGraphic.drawRect(0, 0, 100, 100);
        overlayGraphic.endFill();

        const overlayTexture = PIXIRenderer.renderer.generateTexture(overlayGraphic);
        Game.resource.cacheResource(TNT_MODIFIER_FULL_OVERLAY_KEY, overlayTexture);

        // Needed in case something tries to access the asset via pixi
        PIXI.Texture.addToCache(overlayTexture, TNT_MODIFIER_FULL_OVERLAY_KEY);
    }
}
